import "./WorkflowStep.css";

import { useContext } from "react";

import {
  ArrowDownDarkIcon,
  AsteriskButtonIcon,
  HamburgerMenuGreyIcon,
  RoundedMinusSignIcon,
  RoundedPlusSignIcon,
  SuccessIcon,
} from "../../../../../../../../../assets";
import { ColorConstants, OrganisationTypeConstants } from "../../../../../../../../../constants";
import { WorkflowStepModel } from "../../../../../../../../../models";
import { WorkflowTaskPanel } from "../../../../../../../../../templates/WorkflowTaskPanel";
import { useAuth } from "../../../../../../../../../useAuth";
import { Button, Column, CursorDataGrid, ProfileMenu, ProfileMenuItem, Row } from "../../../../../../../../../widget";
import { TasksBoardContext } from "../../../../TasksBoardContext";
import { DeleteStepModal, EditStepModal } from "./components";
import { useWorkflowStep } from "./useWorkflowStep";

interface WorkflowStepProps {
  workflowStep: WorkflowStepModel;
}

export const WorkflowStep = ({ workflowStep }: WorkflowStepProps): JSX.Element => {
  const { currentUserType } = useAuth();
  const { workflowStageOptions, workflowStageLabel, isRepeatableWorkflowStage } = useContext(TasksBoardContext);
  const {
    showStep,
    handleShowStep,

    workflowUuid,
    tasksBoard,

    stepActionsMenuOptionsList,

    onChange,
    columns,
    isLoadingTasks,
    defaultSortingCriteria,

    refreshTasksBoard,
    shouldRefreshTasksBoard,

    showTaskPanel,
    setShowTaskPanel,

    showStepActionsMenu,
    setShowStepActionsMenu,

    showDeleteStepModal,
    setShowDeleteStepModal,

    showEditStepModal,
    setShowEditStepModal,

    task,
    handleCloseTaskPanel,
    onStepDrop,

    isContributorAndCanAddTasks,
    canEditStep,
  } = useWorkflowStep({
    workflowStep,
  });

  return (
    <div
      className={`WorkflowStep ${currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "Rounded" : ""}`}
      draggable={isContributorAndCanAddTasks && !showStep}
      onDrop={
        isContributorAndCanAddTasks
          ? (e) => onStepDrop(workflowStep.sequence, e.dataTransfer.getData("stepUuid"))
          : undefined
      }
      onDragOver={isContributorAndCanAddTasks ? (ev) => ev.preventDefault() : undefined}
      onDragStart={
        isContributorAndCanAddTasks ? (ev) => ev.dataTransfer.setData("stepUuid", workflowStep.uuid) : undefined
      }
    >
      <Row spacingV={showStep ? "ml" : undefined}>
        <Column span={12} hasNoPadding>
          <div className="WorkflowStepContent">
            <div className="WorkflowStepLeft">
              {!showStep && isContributorAndCanAddTasks && <HamburgerMenuGreyIcon className="HamburgerMenuIcon" />}
              <div className="WorkflowStepText">
                <h2>{workflowStep.name}</h2>
                <div className="WorkflowStepTaskCount">
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {workflowStep.isComplete ? (
                    <div className="StepCompleted">
                      <SuccessIcon />
                      <p className="body2">Step completed</p>
                    </div>
                  ) : workflowStep.taskCount && workflowStep.taskCount > 0 ? (
                    <p className="body2Light">
                      {workflowStep.taskCount} task{workflowStep.taskCount > 1 ? "s" : ""}
                    </p>
                  ) : (
                    <p className="body2Light NoTasks">There are currently no tasks added to this step</p>
                  )}
                  {workflowStep.mandatoryTaskCount && workflowStep.mandatoryTaskCount > 0 ? (
                    <>
                      <p className="body2Light">|</p>
                      <AsteriskButtonIcon className="AsteriskIcon" />
                      <p className="body2">
                        {workflowStep.mandatoryTaskCount} mandatory task
                        {workflowStep.mandatoryTaskCount > 1 ? "s" : ""}
                      </p>
                    </>
                  ) : undefined}
                </div>
              </div>
            </div>
            {((!isContributorAndCanAddTasks && workflowStep.taskCount && workflowStep.taskCount > 0) ||
              isContributorAndCanAddTasks) && (
              <Button
                className="SeeTasksButton"
                text={showStep ? "Hide details" : "Show details"}
                variant="quaternary"
                hasNoPadding
                size="small"
                icon={
                  showStep ? (
                    <RoundedMinusSignIcon className="RoundedMinusSignIcon" />
                  ) : (
                    <RoundedPlusSignIcon className="RoundedPlusSignIcon" />
                  )
                }
                onClick={() => handleShowStep(!showStep)}
              />
            )}
          </div>
        </Column>
      </Row>
      {showStep && (
        <div className="ExpandedWorkflowStepContent">
          <Row>
            <Column span={12} hasNoPadding>
              <CursorDataGrid
                data={[]}
                onChange={onChange}
                dataIsLoading={isLoadingTasks}
                columns={columns}
                clientSide={false}
                hasNoPadding
                hasBottomMargin={false}
                showTotalCount={false}
                filterable
                defaultSortingCriteria={defaultSortingCriteria}
                refresh={refreshTasksBoard}
              />
            </Column>
          </Row>
          {isContributorAndCanAddTasks && !workflowStep.isComplete && (
            <Row>
              <Column span={12} hasNoPadding>
                <div className="WorkflowStepButtons">
                  {canEditStep && (
                    <div className="StepActions">
                      <Button
                        text="Actions"
                        variant="reversedPrimary"
                        hasNoPadding
                        size="small"
                        icon={<ArrowDownDarkIcon stroke={ColorConstants.INDIGO_500} />}
                        onClick={() => setShowStepActionsMenu(true)}
                      />
                      <ProfileMenu
                        show={showStepActionsMenu}
                        onClose={() => setShowStepActionsMenu(false)}
                        position={{ top: "0" }}
                      >
                        {stepActionsMenuOptionsList.map((el) => (
                          <ProfileMenuItem
                            key={el.id}
                            profileMenuItem={el}
                            onClick={() => {
                              if (el.action) {
                                el.action();
                              }
                              setShowStepActionsMenu(false);
                            }}
                          />
                        ))}
                      </ProfileMenu>
                    </div>
                  )}
                  <Button text="Add task" hasNoPadding size="small" onClick={() => setShowTaskPanel(true)} />
                </div>
              </Column>
            </Row>
          )}
        </div>
      )}
      <DeleteStepModal
        show={showDeleteStepModal}
        closeModal={() => setShowDeleteStepModal(false)}
        workflowUuid={workflowUuid}
        workflowStepUuid={workflowStep.uuid}
        workflowStepRowVersion={workflowStep.rowVersion}
      />
      <EditStepModal
        show={showEditStepModal}
        closeModal={() => setShowEditStepModal(false)}
        workflowStep={workflowStep}
      />
      {showTaskPanel && (
        <WorkflowTaskPanel
          task={task}
          currentStepUuid={workflowStep.uuid}
          workflow={tasksBoard?.results[0]}
          show={showTaskPanel}
          phaseOptions={workflowStageOptions}
          phaseLabel={workflowStageLabel}
          isRepeatablePhase={isRepeatableWorkflowStage}
          refresh={refreshTasksBoard}
          handleRefresh={shouldRefreshTasksBoard}
          onClose={handleCloseTaskPanel}
        />
      )}
    </div>
  );
};
